import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AuthConfigService } from "../../logic/auth/auth-config.service";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { Loading } from "../feedback/loading";
import { Alert, Typography } from "@mui/material";
import { AccessTokenContext } from "../../logic/auth/access-token-context";
import { FullHeightContainer } from "../util/full-height-container";
import { PlatformUiEnv } from "../../logic/platform-ui-envs.type";
import { UserContext } from "../../logic/auth/user-context";
import { OrgDto, UserDto, platformApiAudience } from "@arrowsup/platform-dtos";
import { PlatformUiEnvToHostMap } from "../../logic/data-provider/platform-ui-env-to-host-map";
import { usePlatformUiEnv } from "../../logic/env/use-platform-ui-env";
import { fetchUtils } from "react-admin";

interface Props {
  authConfigService: AuthConfigService;
}

const FullHeightLoading: React.FC = () => (
  <FullHeightContainer>
    <Loading />
  </FullHeightContainer>
);

const getLoginOpts = (audience: string) => {
  return {
    audience,
  };
};

/**
 * Silent renewal errors that mean the Auth0 session is gone, so only an
 * interactive login can issue a new token.
 */
const interactiveLoginErrors = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
]);

const needsInteractiveLogin = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "error" in error &&
  typeof error.error === "string" &&
  interactiveLoginErrors.has(error.error);

/** localStorage key for the user's last-selected active org. */
const ACTIVE_ORG_KEY = "au.activeOrgId";

/** Fetch the current user from the backend. */
const getRealUser = async (
  env: PlatformUiEnv,
  accessToken: string
): Promise<UserDto> => {
  const resp = await fetchUtils.fetchJson(
    PlatformUiEnvToHostMap[env] + "/api/users/self",
    {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      }),
    }
  );
  return resp.json as Promise<UserDto>;
};

const AuthRequiredUserContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [userDto, setUserDto] = useState<undefined | UserDto>(undefined);
  const [activeOrg, setActiveOrgState] = React.useState<OrgDto | undefined>(
    undefined
  );
  const [loadErr, setLoadErr] = useState<unknown | undefined>(undefined);

  const env = usePlatformUiEnv();
  const getAccessToken = useContext(AccessTokenContext).getAccessToken;

  useEffect(() => {
    getAccessToken()
      .then((accessToken) => getRealUser(env, accessToken))
      .then((loadedUser) => {
        setUserDto(loadedUser);
        // Restore the last-viewed org, but only if it's still one the user may
        // access (self-heals if a membership was revoked).
        const storedId = Number(localStorage.getItem(ACTIVE_ORG_KEY));
        const match = loadedUser.organizations?.find((o) => o.id === storedId);
        setActiveOrgState(match);
      })
      .catch((e) => setLoadErr(e));
  }, [getAccessToken, env]);

  if (loadErr) {
    return (
      <FullHeightContainer>
        <Alert severity="error">
          Unable to login: {String(loadErr)}
          <a
            href={
              "https://goarrowsup.auth0.com/v2/logout?returnTo=" +
              encodeURIComponent(
                window.location.protocol + "//" + window.location.host
              )
            }
            style={{ paddingLeft: "1em" }}
          >
            Logout
          </a>
        </Alert>
      </FullHeightContainer>
    );
  }

  if (userDto === undefined) {
    return <FullHeightLoading />;
  }

  return (
    <UserContext.Provider
      value={{
        user: userDto,
        activeOrg,
        setActiveOrg: (org: OrgDto) => {
          setActiveOrgState(org);
          localStorage.setItem(ACTIVE_ORG_KEY, String(org.id));
        },
      }}
      // Force redraw w/ new key.
      key={`${userDto.id}-${activeOrg?.id ?? "undefined"}`}
    >
      {children}
    </UserContext.Provider>
  );
};

const AuthRequiredAccessTokenProvider: React.FC<{
  children: React.ReactNode;
  audience: string;
}> = ({ children, audience }) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<unknown | undefined>(undefined);

  // getAccessTokenSilently serves its cached token until it expires, then
  // renews it, so asking per request keeps a long-idle tab working.
  const getAccessToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently(getLoginOpts(audience));
    } catch (e) {
      if (needsInteractiveLogin(e)) {
        void loginWithRedirect(getLoginOpts(audience));
      }
      throw e;
    }
  }, [getAccessTokenSilently, loginWithRedirect, audience]);

  useEffect(() => {
    getAccessToken()
      .then(() => setReady(true))
      .catch(setError);
  }, [getAccessToken]);

  if (error) {
    const errStr = error instanceof Error ? error.message : String(error);
    return (
      <Alert severity="error">Unable to get user credentials: {errStr}</Alert>
    );
  } else if (!ready) {
    return <FullHeightLoading />;
  } else {
    return (
      <AccessTokenContext.Provider value={{ getAccessToken }}>
        <AuthRequiredUserContextProvider>
          {children}
        </AuthRequiredUserContextProvider>
      </AccessTokenContext.Provider>
    );
  }
};

const AuthRequiredChildWrapper: React.FC<{
  children: React.ReactNode;
  audience: string;
}> = ({ children, audience }) => {
  const auth0 = useAuth0();

  if (auth0.isLoading) {
    return <FullHeightLoading />;
  } else if (auth0.isAuthenticated) {
    return (
      <AuthRequiredAccessTokenProvider audience={audience}>
        {children}
      </AuthRequiredAccessTokenProvider>
    );
  } else {
    if (window.location.href.includes("oauth")) {
      void auth0.loginWithPopup(getLoginOpts(audience)); // Redirects.
    } else {
      void auth0.loginWithRedirect(getLoginOpts(audience));
    }
    return <Typography variant="body1">Redirecting to login</Typography>;
  }
};

/**
 * Requires authentication to render children and provides
 * children with UserContext and AccessTokenContext.
 *
 * Displays a loading spinner while loading.  Automatically
 * redirects to login if not authenticated.
 */
export const AuthRequired: React.FC<React.PropsWithChildren<Props>> = ({
  authConfigService,
  children,
}) => {
  const config = authConfigService.config();
  const audience = platformApiAudience(PlatformUiEnvToHostMap[config.env]);

  return (
    <Auth0Provider
      domain={config.auth0Domain}
      clientId={config.auth0ClientId}
      redirectUri={window.location.origin}
      audience={audience}
    >
      <AuthRequiredChildWrapper audience={audience}>
        {children}
      </AuthRequiredChildWrapper>
    </Auth0Provider>
  );
};
